import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';
import { environment } from 'environments/environment';

export interface FevRow {
    ID: number;
    KEMP: number;
    KFCO: number;
    KDOC: number;
    TIPO: string;
    ORIG: string;
    STAT: string;
    FEAJ: string;
    FPRO: string;
    FREA: string;
    PERI: string;
    MONT: number;
    MREA: number;
    PROB: number;
    CONF: number;
    DETE: string;
    MOTI: string;
    FCO_NAME: string;
    KCCO: number;
    CCO_NUME: number;
    CCO_NAME: string;
    CCO_CLI_NAME: string;
    TDOC: string;
    NREF: string;
    NUME: number;
    REPR: number;
    SALDO: number;   // saldo acumulado (running balance) calculado en backend
}

export interface FevPage {
    data: FevRow[];
    meta: { page: number; take: number; itemCount: number; pageCount: number };
}

@Injectable({ providedIn: 'root' })
export class FevService {
    // Emite cada vez que un evento se crea/edita/elimina/reprograma, para que
    // pantallas ajenas (ej. el contador de "Flujos de caja proyectados vencidos"
    // del layout) puedan recalcularse sin tener que sondear.
    readonly changed$ = new Subject<void>();

    constructor(private _http: HttpClient) {}

    list(
        kemp: number,
        page = 1,
        take = 100,
        fec0?: Date,
        fec1?: Date,
        stat?: string,
        tipo?: string,
        incluirInactivos = false,
        sortDir: 'ASC' | 'DESC' = 'DESC',
        q?: string,
        kcco?: number[],
    ): Observable<FevPage> {
        const p: any = { page: String(page), take: String(take), sort: sortDir };
        if (fec0) p.fec0 = this._fmt(fec0);
        if (fec1) p.fec1 = this._fmt(fec1);
        if (stat && stat !== 'ALL') p.stat = stat;
        if (tipo && tipo !== 'ALL') p.tipo = tipo;
        if (incluirInactivos) p.acti = 'TODAS';
        if (q && q.trim()) p.q = q.trim();
        if (kcco && kcco.length > 0) p.kcco = kcco.join(',');
        return this._http.get<FevPage>(`${environment.urlApi}/api/fev/list/${kemp}`, { params: p });
    }

    getCFlowSaldAt(kemp: number, fecha: string): Observable<number> {
        return new Observable(obs => {
            this._http.get<{ saldo: number }>(
                `${environment.urlApi}/api/fev/sald-at/${kemp}`,
                { params: { fecha } },
            ).subscribe({
                next: r => { obs.next(r.saldo); obs.complete(); },
                error: e => obs.error(e),
            });
        });
    }

    ajustarFeaj(kemp: number, id: number, feaj: string): Observable<{ ok: boolean; id: number; feaj: string; peri: string }> {
        return this._http.patch<{ ok: boolean; id: number; feaj: string; peri: string }>(
            `${environment.urlApi}/api/fev/${id}/feaj`,
            { kemp, feaj },
        ).pipe(tap(() => this.changed$.next()));
    }

    ajustarSaldoInicial(kemp: number): Observable<{
        ok: boolean;
        anterior?: number;
        nuevo?: number;
        diferencia?: number;
        saldoBanco?: number;
        saldoFevHoy?: number;
        message?: string;
    }> {
        return this._http.post<any>(`${environment.urlApi}/api/fev/ajustar-saldo-inicial/${kemp}`, {});
    }

    update(id: number, payload: {
        feaj: string;
        tipo: 'IN' | 'OUT';
        kfco: number;
        mont: number;
        dete?: string;
        kcco?: number | null;
    }): Observable<{ ok: boolean }> {
        return this._http.patch<{ ok: boolean }>(`${environment.urlApi}/api/fev/${id}`, payload)
            .pipe(tap(() => this.changed$.next()));
    }

    create(payload: {
        kemp: number;
        kfco: number;
        tipo: 'IN' | 'OUT';
        feaj: string;
        mont: number;
        dete?: string;
    }): Observable<{ id: number }> {
        return this._http.post<{ id: number }>(`${environment.urlApi}/api/fev`, payload)
            .pipe(tap(() => this.changed$.next()));
    }

    reprogramar(id: number, payload: {
        nuevaFecha: string;
        moti: string;
        note?: string;
        kusr?: number;
    }): Observable<{ ok: boolean }> {
        return this._http.post<{ ok: boolean }>(`${environment.urlApi}/api/fev/${id}/reprogramar`, payload)
            .pipe(tap(() => this.changed$.next()));
    }

    delete(id: number): Observable<any> {
        return this._http.delete(`${environment.urlApi}/api/fev/${id}`)
            .pipe(tap(() => this.changed$.next()));
    }

    private _fmt(d: Date): string {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
}
